import { ampli } from 'lib-shared-tracking';
import type {
  SearchFilterCondition,
  SearchFilterStrategy,
} from 'lib-shared-types-api';
import { PropsWithChildren } from 'lib-shared-types-utils';
import mapKeys from 'lodash/mapKeys';
import noop from 'lodash/noop';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useList, useMap, useUpdateEffect } from 'react-use';

import { useAppSelector } from 'utils/redux/useAppSelector';

import { isDateFilterField } from '../../utils/date/isDateFilterField';
import { isNullishFilterOperator } from '../../utils/filters/filterTypeGuards';

import {
  fieldOperators,
  filtersInitialState,
  filterValidationErrors,
} from './utils/definitions';

/** Represents the options arg for copying a condition. */
type CopyConditionOptions = {
  fullCopy: boolean;
};

export type SearchFiltersValidationErrorKey =
  keyof typeof filterValidationErrors;

/** Represents the filters validation state. */
type SearchFiltersValidation = {
  errors: Record<number, SearchFiltersValidationErrorKey>;
  isEmpty: boolean;
  isValid: boolean;
  validate: () => boolean;
};

/** Represents the props that must be passed to the provider. */
type SearchFiltersProviderProps = PropsWithChildren;

/** Represents the shape of the context value. */
type SearchFiltersContextType = {
  strategy: SearchFilterStrategy;
  conditions: SearchFilterCondition[];
  validation: SearchFiltersValidation;
  addCondition: (condition: SearchFilterCondition) => void;
  updateCondition: (index: number, condition: SearchFilterCondition) => void;
  copyCondition: (
    index: number,
    condition: SearchFilterCondition,
    options: CopyConditionOptions,
  ) => void;
  removeCondition: (index: number) => void;
  updateStrategy: (strategy: SearchFilterStrategy) => void;
  reset: VoidFunction;
};

/** The initial context value. */
export const initialContextValue: SearchFiltersContextType = {
  strategy: filtersInitialState.strategy,
  conditions: filtersInitialState.conditions,
  validation: {
    errors: {},
    isEmpty: false,
    isValid: true,
    validate: () => false,
  },
  addCondition: noop,
  updateCondition: noop,
  copyCondition: noop,
  removeCondition: noop,
  updateStrategy: noop,
  reset: noop,
};

/** The context. */
export const SearchFiltersContext =
  createContext<SearchFiltersContextType>(initialContextValue);

/** The context provider. All its children will have access to its value. */
export function SearchFiltersProvider({
  children,
}: SearchFiltersProviderProps): JSX.Element {
  const filters = useAppSelector((state) => state.search.params.filters);
  const [strategy, setStrategy] = useState(filters.strategy);
  const [conditions, conditionSetters] = useList(filters.conditions);
  const [errors, errorSetters] = useMap<SearchFiltersValidation['errors']>({});

  /**
   * Updates the strategy and conditions when the external state is updated,
   * e.g. when applying a saved search.
   */
  useUpdateEffect(() => {
    setStrategy(filters.strategy);
    conditionSetters.set(filters.conditions);
  }, [filters.strategy, filters.conditions]);

  /** Sets the new strategy and sends a tracking event */
  const updateStrategy = useCallback((strategy: SearchFilterStrategy) => {
    setStrategy(strategy);
    ampli.searchFilterStrategyUpdated({ strategy });
  }, []);

  /**
   * Checks whether a condition is valid according to its field, operator and
   * values.
   *
   * @returns {string | null} The error message if invalid, or nothing if valid
   */
  const validateCondition = useCallback(
    ({
      field,
      operator,
      values,
    }: SearchFilterCondition): SearchFiltersValidationErrorKey | null => {
      if (isDateFilterField(field)) {
        if (operator === 'between') {
          const isInvalid =
            !values.length ||
            (values.length === 2 && values.some(({ value }) => !value));
          return isInvalid ? 'invalidRange' : null;
        }
        const isInvalid = !values.length;
        return isInvalid ? 'noDate' : null;
      }

      // For all other fields we just check that values are provided if the operator requires them.
      const isInvalid = !isNullishFilterOperator(operator) && !values.length;
      return isInvalid ? 'noValue' : null;
    },
    [],
  );

  /**
   * Validates each condition and if the validator returns an error, it adds it
   * to the errors state.
   *
   * @returns {boolean} `false` if at least one condition is invalid, otherwise
   *   `true`
   */
  const validateConditions = useCallback(() => {
    const invalidConditions = conditions.reduce((acc, condition, index) => {
      const errorKey = validateCondition(condition);
      if (errorKey) acc[index] = errorKey;
      return acc;
    }, {} as SearchFiltersValidation['errors']);

    const isValid = Object.keys(invalidConditions).length === 0;

    if (!isValid) {
      errorSetters.setAll(invalidConditions);

      ampli.searchFiltersValidationFailed({
        errors: mapKeys(
          invalidConditions,
          (_errorKey, index) => conditions[parseInt(index)].field,
        ),
      });
    }

    return isValid;
  }, [conditions, errorSetters, validateCondition]);

  /** Adds a new condition to the state. */
  const addCondition = useCallback(
    (condition: SearchFilterCondition) => {
      conditionSetters.push(condition);

      ampli.searchFilterConditionAdded({
        location: 'add_more',
        type: condition.field,
      });
    },
    [conditionSetters],
  );

  /**
   * Updates an existing condition in the state using its index value. If the
   * condition was previously invalid, and is now valid, the previous error is
   * cleared.
   */
  const updateCondition = useCallback(
    (index: number, condition: SearchFilterCondition) => {
      conditionSetters.updateAt(index, condition);

      const isValid = !validateCondition(condition);
      if (isValid) errorSetters.remove(index);
    },
    [conditionSetters, errorSetters, validateCondition],
  );

  /**
   * Creates a copy of an existing condition and inserts it immediately after
   * the condition being copied. If `options.fullCopy` is `false`, only the
   * `field` is copied. If `true`, the `operator` and `values` are copied as
   * well.
   */
  const copyCondition = useCallback(
    (
      index: number,
      condition: SearchFilterCondition,
      options: CopyConditionOptions,
    ) => {
      const newCondition = {
        ...condition,
        ...(!options.fullCopy
          ? {
              operator: fieldOperators[condition.field].defaultOperator,
              values: [],
            }
          : {}),
      } as SearchFilterCondition;
      conditionSetters.insertAt(index + 1, newCondition);

      ampli.searchFilterConditionAdded({
        location: options.fullCopy ? 'duplicate' : 'add_another',
        type: condition.field,
      });
    },
    [conditionSetters],
  );

  /**
   * Removes a condition from the state by its index value and clears any
   * validation errors.
   */
  const removeCondition = useCallback(
    (index: number) => {
      const { field } = conditions[index];
      ampli.searchFilterConditionRemoved({ type: field });

      conditionSetters.removeAt(index);
      errorSetters.remove(index);
    },
    [conditions, conditionSetters, errorSetters],
  );

  /** Resets everything to the initial state. */
  const reset = useCallback(() => {
    setStrategy(filtersInitialState.strategy);
    conditionSetters.set(filtersInitialState.conditions);
    errorSetters.reset();
  }, [conditionSetters, errorSetters]);

  const contextValue: SearchFiltersContextType = useMemo(
    () => ({
      strategy,
      conditions,
      validation: {
        errors,
        isEmpty: !conditions.length,
        isValid: !Object.keys(errors).length,
        validate: validateConditions,
      },
      addCondition,
      updateCondition,
      copyCondition,
      removeCondition,
      updateStrategy,
      reset,
    }),
    [
      addCondition,
      conditions,
      copyCondition,
      errors,
      removeCondition,
      reset,
      strategy,
      updateCondition,
      updateStrategy,
      validateConditions,
    ],
  );

  return (
    <SearchFiltersContext.Provider value={contextValue}>
      {children}
    </SearchFiltersContext.Provider>
  );
}

/** A hook exposing the context functionality. */
export function useSearchFilters(): SearchFiltersContextType {
  return useContext(SearchFiltersContext);
}
